import { useEffect, useMemo, useState } from "react";
import { loadStripe, type Stripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { ShieldCheck, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useCreateStripeIntent } from "@/hooks/use-stripe";

interface StripeCheckoutModalProps {
  jobId: string;
  amountUsdc: number;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

// Inner form — rendered inside <Elements>, so useStripe()/useElements() are live.
function CheckoutForm({
  amountUsdc,
  onAuthorized,
}: {
  amountUsdc: number;
  onAuthorized: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [ready, setReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError(null);

    try {
      // Validate the card field first — returns fast with a clear message if it
      // is empty/incomplete, so we never get stuck on "Authorizing…".
      const submitResult = await elements.submit();
      if (submitResult.error) {
        setError(submitResult.error.message ?? "Please check your card details");
        setSubmitting(false);
        return;
      }

      // Manual-capture intent: confirming authorises (holds) the card without
      // capturing. Stripe fires amount_capturable_updated -> our webhook sets the
      // job to AUTHORIZED. We capture later, on job approval.
      const { error: confirmError } = await stripe.confirmPayment({
        elements,
        redirect: "if_required",
      });

      if (confirmError) {
        setError(confirmError.message ?? "Card authorization failed");
        setSubmitting(false);
        return;
      }

      onAuthorized();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Something went wrong authorizing your card. Please try again.",
      );
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {!ready && (
        <div className="flex items-center justify-center py-8 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading secure card
          field…
        </div>
      )}
      <div className={ready ? "block" : "hidden"}>
        <PaymentElement
          onReady={() => setReady(true)}
          onLoadError={(e) =>
            setError(
              e?.error?.message ??
                "The card field failed to load. Please close and try again.",
            )
          }
        />
      </div>

      {error && (
        <div className="flex items-start gap-2 text-sm text-red-400">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex items-start gap-2 rounded-lg border border-emerald-500/25 bg-emerald-500/5 p-3">
        <ShieldCheck className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
        <p className="text-xs text-muted-foreground">
          We place a <span className="text-foreground">hold</span> for ${amountUsdc},
          we don't charge it. The money is only captured when you approve the
          delivered work — and released back to you if you reject it.
        </p>
      </div>

      <Button
        type="submit"
        disabled={!stripe || !ready || submitting}
        className="w-full"
      >
        {submitting ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Authorizing…
          </>
        ) : (
          `Hold $${amountUsdc} on card`
        )}
      </Button>
    </form>
  );
}

export function StripeCheckoutModal({
  jobId,
  amountUsdc,
  open,
  onClose,
  onSuccess,
}: StripeCheckoutModalProps) {
  const createIntent = useCreateStripeIntent();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [stripePromise, setStripePromise] =
    useState<Promise<Stripe | null> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [authorized, setAuthorized] = useState(false);

  // When the modal opens, mint a PaymentIntent and load Stripe.js with the
  // server-provided publishable key (no client env var needed).
  useEffect(() => {
    if (!open || clientSecret) return;
    let cancelled = false;
    createIntent
      .mutateAsync({ jobId })
      .then((res) => {
        if (cancelled) return;
        if (!res.clientSecret || !res.publishableKey) {
          setError("Card payments are not configured yet. Please try again later.");
          return;
        }
        setClientSecret(res.clientSecret);
        setStripePromise(loadStripe(res.publishableKey));
      })
      .catch((e) => {
        if (cancelled) return;
        setError(
          e?.response?.data?.message ??
            "Could not start card payment. Please try again.",
        );
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, jobId]);

  const options = useMemo(
    () =>
      clientSecret
        ? { clientSecret, appearance: { theme: "night" as const } }
        : undefined,
    [clientSecret],
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent className="sm:max-w-md">
        {authorized ? (
          // Success state — a clear confirmation with a custom icon, not a toast.
          <div className="flex flex-col items-center text-center py-4">
            <div className="h-16 w-16 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mb-4">
              <CheckCircle2 className="h-8 w-8 text-emerald-400" />
            </div>
            <h3 className="font-display text-xl font-bold mb-1">
              Card authorized
            </h3>
            <p className="text-sm text-muted-foreground max-w-xs mb-6">
              Your job is funded and live. We've placed a hold for ${amountUsdc} —
              it's only captured when you approve the delivered work, and released
              back if you reject it.
            </p>
            <Button className="w-full" onClick={onSuccess}>
              View your job
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Pay by card</DialogTitle>
              <DialogDescription>
                Enter your card to fund this job. It's held in escrow and only
                charged when you approve the work — no crypto needed.
              </DialogDescription>
            </DialogHeader>

            {error && !clientSecret && (
              <div className="flex items-start gap-2 text-sm text-red-400 py-4">
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            {!clientSecret && !error && (
              <div className="flex items-center justify-center py-10 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin mr-2" /> Preparing secure
                checkout…
              </div>
            )}

            {clientSecret && stripePromise && options && (
              <Elements stripe={stripePromise} options={options}>
                <CheckoutForm
                  amountUsdc={amountUsdc}
                  onAuthorized={() => setAuthorized(true)}
                />
              </Elements>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
